import React, { CSSProperties, ReactNode, RefObject, useEffect, useLayoutEffect, useRef, useState } from "react"
import styles from "./ContentBubble.module.scss"
import { createPortal } from "react-dom"

const HEADER_HEIGHT = 105

interface Props {
  /** Contents of the `ContentBubble` */
  children: ReactNode
  /** Reference to an `HTMLElement` the `ContentBubble` will position at */
  target: RefObject<HTMLElement | null>
  /** Reference to an `HTMLElement` to which arrow should point, if not provided
   * then arrow will point at `target`
   */
  arrowTarget?: RefObject<HTMLElement | null>
  /** An array of preferred placements of `ContentBubble` relative to the target,
   * if provided then `ContentBubble` will check these before remaining placements
   * from the default placement list:
   *
   * `[Placement.TOP, Placment.BOTTOM, Placement.LEFT, Placement.RIGHT]`
   */
  preferredPlacements?: Array<Placement>
  /** Set `ContentBubble` visible when true */
  show?: boolean
  /** Triggered when user clicks outside `ContentBubble` indicating intent to close it */
  close?: () => void
}

export enum Placement { TOP, BOTTOM, RIGHT, LEFT }

export function ContentBubble({
  children,
  target,
  arrowTarget,
  show,
  preferredPlacements,
  close,
}: Props) {
  const [windowSize, setWindowSize] = useState([0, 0])
  const [bubbleSize, setBubbleSize] = useState([0, 0])
  const bubbleContainer = useRef<HTMLDivElement>(null)
  const [bubbleStyle, setBubbleStyle] = useState<React.CSSProperties>({})
  const [arrowStyle, setArrowStyle] = useState<React.CSSProperties>({})

  useLayoutEffect(() => {
    if (!bubbleContainer.current || !target.current || !bubbleContainer.current.parentElement) {
      setBubbleStyle({visibility: "hidden"})
      setArrowStyle({visibility: "hidden"})
      return
    }
    const targetBox = target.current.getBoundingClientRect()
    const documentBox = document.documentElement.getBoundingClientRect()
    const bubbleBox = bubbleContainer.current.getBoundingClientRect()

    // Arrow target offset calculation
    const arrowTargetBox = arrowTarget?.current?.getBoundingClientRect()
    const offsetX = arrowTargetBox
      ? (arrowTargetBox.left + arrowTargetBox.width/2) - (targetBox.left + targetBox.width/2)
      : 0
    const offsetY = arrowTargetBox
      ? (arrowTargetBox.top + arrowTargetBox.height/2) - (targetBox.top + targetBox.height/2)
      : 0

    // List of placement priorities
    const placements: Array<Placement> = []
    // Set preferred user (of the component) defined placement priorities
    if (preferredPlacements) {
      placements.push(...preferredPlacements)
    }
    // Fill missing placement options
    const defaultPlacements = [
      Placement.TOP,
      Placement.BOTTOM,
      Placement.LEFT,
      Placement.RIGHT,
    ]
    for (const defaultPlacement of defaultPlacements) {
      if (!placements.includes(defaultPlacement)) placements.push(defaultPlacement)
    }

    // Inherit increased z-index and fixed position configuration if present in any ancestor.
    // Highest z-index is found and upped by 1 to ensure bubble is above whatever it is attached to,
    // and by default "absolute" position is used relative to body, but fixed position is necessary
    // if the structure it is attached to has fixed positioning (else bubble will scroll away with body)
    let parent = target.current.parentElement
    let zIndex = 0
    let isFixed = false
    while (parent) {
      const parentZ = window.getComputedStyle(parent).zIndex
      const parentPos = window.getComputedStyle(parent).position
      if (!isNaN(Number(parentZ))) zIndex = Math.max(Number(parentZ), zIndex)
      if (parentPos === "fixed") isFixed = true
      parent = parent.parentElement
    }
    const inheritedProps: CSSProperties = {
      zIndex: zIndex + 1,
      position: isFixed ? "fixed" : "absolute",
    }
    const scroll = isFixed ? 0 : window.scrollY

    // Now that placements are ordered by priority - iterate through and find first valid one
    for (const placement of placements) {
      switch(placement) {
        case Placement.TOP:
          if (bubbleBox.height + HEADER_HEIGHT <= targetBox.top) {
            setBubbleStyle({
              top: `${scroll + targetBox.top - bubbleBox.height}px`,
              left: `${targetBox.left + targetBox.width/2 - bubbleBox.width/2}px`,
              ...inheritedProps,
            })
            setArrowStyle({
              bottom: "0px",
              left: `${Math.min(Math.max(bubbleBox.width/2 + offsetX, 50), bubbleBox.width - 50)}px`,
              transform: "rotate(180deg) translate(50%, 0)",
            })
            return
          }
          break
        case Placement.BOTTOM:
          if (bubbleBox.height <= documentBox.height - targetBox.bottom) {
            setBubbleStyle({
              top: `${scroll + targetBox.bottom}px`,
              left: `${targetBox.left - bubbleBox.width/2 + targetBox.width/2}px`,
              ...inheritedProps,
            })
            setArrowStyle({
              top: "0px",
              left: `${Math.min(Math.max(bubbleBox.width/2 + offsetX, 50), bubbleBox.width - 50)}px`,
              transform: "translate(-50%, 0)",
            })
            return
          }
          break
        case Placement.RIGHT:
          // Fix for bubble going out of screen because target is smaller and too close to top of page
          const outOfBoundsOffset = Math.max(
            0,
            (targetBox.top - bubbleBox.height/2 + targetBox.height/2 - HEADER_HEIGHT) * -1
          )
          if (bubbleBox.width <= documentBox.width - targetBox.right) {
            setBubbleStyle({
              top: `${scroll + targetBox.top - bubbleBox.height/2 + targetBox.height/2 + outOfBoundsOffset}px`,
              left: `${targetBox.right}px`,
              ...inheritedProps,
            })
            setArrowStyle({
              left: "0px",
              top: `${Math.min(Math.max(bubbleBox.height/2 + offsetY - outOfBoundsOffset, 50), bubbleBox.height - 50)}px`,
              transform: "rotate(270deg) translate(50%, 0)",
            })
            return
          }
          break
        case Placement.LEFT:
          if (bubbleBox.width <= targetBox.left) {
            setBubbleStyle({
              top:`${scroll + targetBox.top - bubbleBox.height/2 + targetBox.height/2}px`,
              left: `${targetBox.left - bubbleBox.width}px`,
              ...inheritedProps,
            })
            setArrowStyle({
              right: "0px",
              top: `${Math.min(Math.max(bubbleBox.height/2 + offsetY, 50), bubbleBox.height - 50)}px`,
              transform: "rotate(90deg) translate(-50%, 0)",
            })
            return
          }
          break
      }
    }
  }, [
    preferredPlacements,
    target,
    arrowTarget,
    bubbleSize,
    windowSize,
  ])

  // Captures information about window size changes which will trigger re-calculation
  // of the ContentBubble placement
  useLayoutEffect(() => {
    function updateSize() {
      setWindowSize(() => [window.innerWidth, window.innerHeight])
    }
    window.addEventListener("resize", updateSize)
    updateSize()
    return () => window.removeEventListener("resize", updateSize)
  }, [])

  // Captures information about bubble contents size and triggers re-calculation
  useEffect(() => {
    if (!bubbleContainer.current) return
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        setBubbleSize(() => [width, height])
      }
    })
    observer.observe(bubbleContainer.current)
    return () => observer.disconnect()
  }, [])

  // Detect clicks outside ContentBubble and call close()
  useEffect(() => {
    function closeBubble(event: MouseEvent) {
      if (bubbleContainer.current && !bubbleContainer.current.contains(event.target as Node)) {
        close && close()
      }
    }
    document.addEventListener("mousedown", closeBubble)
    return () => document.removeEventListener("mousedown", closeBubble)
  }, [close])

  return createPortal(<div
    className={styles.container}
    ref={bubbleContainer}
    style={{
      visibility: show ? "visible" : "hidden",
      opacity: show ? "1" : "0",
      ...bubbleStyle,
    }}
  >
    <div className={styles.bubble}>
      {children}
    </div>
    <div
      className={styles.triangle}
      style={arrowStyle}
    >
      <div/>
    </div>
  </div>
  , document.body)
}